import { API_BASE_URL } from '../config';

/**
 * Shared API client adapter for all authenticated backoffice requests.
 *
 * Handles:
 * - Authorization header injection
 * - HTTP error detection with structured error messages
 * - 204 No Content responses
 * - ResponseWrapperFilter envelope unwrapping ({ success, data, error })
 *
 * Not intended for:
 * - Auth endpoints (login/refresh) — use auth.service.ts directly
 * - Binary/Blob responses (PDF downloads) — use raw fetch with Authorization header
 */
export async function apiRequest<T>(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const json = await res.json().catch(() => undefined);
    const message = json?.error?.message || json?.error || `${res.status}: ${res.statusText}`;
    throw new Error(message);
  }

  if (res.status === 204) return null as T;

  const json = await res.json();

  // Unwrap ResponseWrapperFilter envelope: { success, data, error }
  if (json && typeof json === 'object' && 'data' in json) {
    if (!json.success && json.error) {
      throw new Error(typeof json.error === 'string' ? json.error : json.error?.message || 'Erro desconhecido');
    }
    return json.data as T;
  }

  return json as T;
}
